import { useState, type ReactNode } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { Button, Modal } from '@systemeio/components'
import { ArrowLeftIcon, Menu01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { MaintenanceBanner } from 'shared/components/maintenance-banner'
import {
  MobilePublicLanguageSwitcher,
  PublicDashboardLanguageSwitcher,
} from 'shared/components/public-dashboard-language-switcher'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import { ScreenHeightDiv } from './screen-height-div'

interface Props {
  logoSrc?: string
}

function PublicDashboardHeader({ logoSrc }: Props): ReactNode {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const { brand } = useBrand()
  const isMaintenanceBannerEnabled = useFeatureIsOn(featureFlagKeys.enableMaintenanceBanner)

  const close = (): void => setIsOpen(false)

  return (
    <>
      <nav className="top-0 z-10 bg-bg-primary font-inter text-text-primary">
        <div className="z-10 mx-auto flex h-[69px] w-full max-w-436.75 items-center justify-between gap-5 px-5 sm:px-6 md:px-14">
          <div className="flex items-center gap-5">
            <div className="rounded-full ring-bg-primary ring-offset-0 focus-visible:ring-2">
              <img
                className="max-h-[35px] max-w-[150px]"
                src={logoSrc || brand.logo?.path}
                alt={brand.name}
              />
            </div>
          </div>
          <div className={`flex w-full items-center justify-end md:hidden`}>
            <Button
              type="button"
              aria-label="Open Menu"
              variant="ghost-gray"
              size="icon-sm"
              onClick={() => setIsOpen(true)}
            >
              <Menu01Icon aria-hidden="true" className="size-5" />
            </Button>
          </div>
          <div className={`hidden h-full items-center justify-around gap-4 md:flex`}>
            <PublicDashboardLanguageSwitcher />
          </div>
        </div>
        {isMaintenanceBannerEnabled && <MaintenanceBanner data-from="public-dashboard-header" />}
      </nav>
      <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
        <Modal.Popup
          showCloseButton={false}
          viewportProps={{ className: 'block p-0' }}
          className="fixed top-0 right-0 z-10 ml-auto flex min-h-0 max-w-none min-w-68 flex-col overflow-auto rounded-none border-0 bg-bg-primary px-4 py-5 text-text-primary"
        >
          <ScreenHeightDiv className="flex min-w-68 flex-col overflow-auto">
            <Button
              type="button"
              variant="link-gray"
              className="justify-start! gap-2! text-inherit! [&_svg]:size-4!"
              onClick={close}
            >
              <ArrowLeftIcon className="size-5 stroke-current" aria-hidden="true" />
              <span>{t('mobile_nav.back_to_page')}</span>
            </Button>
            <div className="mt-auto flex flex-col gap-3">
              <div className="flex items-center">
                <div className="ml-auto">
                  <MobilePublicLanguageSwitcher />
                </div>
              </div>
            </div>
          </ScreenHeightDiv>
        </Modal.Popup>
      </Modal.Root>
    </>
  )
}

export default PublicDashboardHeader
